import cn from 'classnames';
import { FabricObject } from 'fabric';
import React, { useEffect, useState } from 'react';

import useFabricCanvas from 'editor/src/util/useFabricCanvas';

import getRotationSnapCue from './getRotationSnapCue';

import styles from './index.module.scss';

// How far the level line reaches past the element, in screen pixels.
const LINE_OVERHANG = 24;
// Gap between the rotate handle and the angle badge, in screen pixels.
const BADGE_OFFSET = 16;

type Cue = {
  angle: number;
  snapped: boolean;
  center: { x: number; y: number };
  lineLength: number;
  badge: { x: number; y: number };
};

function computeCue(target: FabricObject): Cue | undefined {
  const { oCoords } = target;
  if (!oCoords) {
    return undefined;
  }

  const { tl, br, mtr } = oCoords;
  const center = { x: (tl.x + br.x) / 2, y: (tl.y + br.y) / 2 };
  const diagonal = Math.hypot(br.x - tl.x, br.y - tl.y);

  return {
    ...getRotationSnapCue(target.angle),
    center,
    lineLength: diagonal + LINE_OVERHANG * 2,
    badge: { x: mtr.x + BADGE_OFFSET, y: mtr.y - BADGE_OFFSET },
  };
}

/**
 * GAPI-61780: feedback while the rotate handle is dragged. Shows the current
 * angle next to the handle; on a snap step the badge turns magenta and a dashed
 * level line crosses the element, in the colour of the existing position snap
 * guides.
 */
function RotationSnapCue() {
  const fabricCanvas = useFabricCanvas();
  const [cue, setCue] = useState<Cue | undefined>();

  useEffect(() => {
    const disposeRotating = fabricCanvas.on('object:rotating', ({ target }) => {
      setCue(computeCue(target));
    });
    const disposeMouseUp = fabricCanvas.on('mouse:up', () => setCue(undefined));

    return () => {
      disposeRotating();
      disposeMouseUp();
      setCue(undefined);
    };
  }, [fabricCanvas]);

  if (!cue) {
    return null;
  }

  return (
    <>
      {cue.snapped && (
        <div
          className={cn(styles.levelLine, 'cy-rotation-snap-line')}
          style={{
            width: cue.lineLength,
            transform: `translate3d(${cue.center.x}px,${cue.center.y}px,0) translateX(-50%) rotate(${cue.angle}deg)`,
          }}
        />
      )}
      <div
        className={cn(styles.badge, { [styles.snapped]: cue.snapped }, 'cy-rotation-snap-badge')}
        style={{ transform: `translate3d(${cue.badge.x}px,${cue.badge.y}px,0)` }}
      >
        {`${cue.angle}°`}
      </div>
    </>
  );
}

export default React.memo(RotationSnapCue);
